import { useState } from "react";
import { LocateFixed, Undo2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { AREA_COLORS } from "./mapParts";
import { DeliveryMapEditor } from "./DeliveryMapEditor";
import { RadiusList, AreaList } from "./AreaLists";

export function DeliveryAreasPanel({ s, set, tenant }) {
  const [editing, setEditing] = useState(null);
  const [focus, setFocus] = useState(null);
  const [busy, setBusy] = useState(false);
  const areas = s.delivery_areas || [];
  const loc = s.store_location || {};
  const setAreas = (v) => set("delivery_areas", v);
  const patchArea = (i, patch) => setAreas(areas.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const setStore = ([lat, lng]) => set("store_location", { lat, lng });
  const locate = async () => {
    const e = tenant.endereco || {};
    setBusy(true);
    try {
      const { data } = await api.post(`/public/menu/${tenant.menu_slug}/geocode`, { street: e.logradouro || "", number: e.numero || "", district: e.bairro || "", city: e.cidade || "", state: e.uf || "" });
      setStore([data.lat, data.lng]); setFocus([data.lat, data.lng]);
      toast.success(data.precision === "endereco" ? "Loja localizada no mapa" : "Localização aproximada: arraste o marcador até a porta da loja");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const newArea = () => {
    const i = areas.length;
    setAreas([...areas, { name: `Área ${i + 1}`, fee: "", eta: "", color: AREA_COLORS[i % AREA_COLORS.length], points: [] }]);
    setEditing(i);
  };
  const editPoints = (fn) => patchArea(editing, { points: fn(areas[editing].points) });
  return (
    <div className="space-y-4" data-testid="delivery-map-panel">
      <div className="card-surface p-5 grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="eyebrow">Localização da loja</p>
          <p className="text-sm text-muted-foreground" data-testid="store-location-text">{loc.lat != null ? `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)} · arraste o marcador escuro para ajustar` : "Clique no mapa onde fica a loja ou localize pelo endereço cadastrado em Minha empresa."}</p>
          <Button variant="outline" size="sm" onClick={locate} disabled={busy} data-testid="locate-store-btn"><LocateFixed className="h-4 w-4 mr-1" />Localizar pelo endereço da empresa</Button>
        </div>
        <label className="flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm self-start">
          <span>Cliente pode ajustar o ponto de entrega no mapa<span className="block text-xs text-muted-foreground">Desligado: vale só o endereço digitado</span></span>
          <Switch checked={s.delivery_pin_adjust !== false} onCheckedChange={(v) => set("delivery_pin_adjust", v)} data-testid="pin-adjust-switch" />
        </label>
      </div>
      {editing != null && areas[editing] && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm flex flex-wrap items-center gap-2" data-testid="drawing-banner">
          <span className="flex-1 min-w-[220px]">Desenhando <b>{areas[editing].name}</b>: clique no mapa para marcar os pontos (mínimo 3) e arraste para ajustar.</span>
          <Button size="sm" variant="outline" onClick={() => editPoints((p) => p.slice(0, -1))} disabled={!areas[editing].points.length} data-testid="undo-point-btn"><Undo2 className="h-4 w-4 mr-1" />Desfazer ponto</Button>
          <Button size="sm" onClick={() => setEditing(null)} data-testid="finish-area-btn"><Check className="h-4 w-4 mr-1" />Concluir</Button>
        </div>
      )}
      <DeliveryMapEditor store={loc} radii={s.delivery_radii || []} areas={areas} editing={editing} focus={focus} onStore={setStore}
        onAddPoint={(p) => editPoints((pts) => [...pts, p])} onMovePoint={(j, p) => editPoints((pts) => pts.map((x, k) => (k === j ? p : x)))} />
      <div className="grid lg:grid-cols-2 gap-4">
        <RadiusList radii={s.delivery_radii || []} onChange={(v) => set("delivery_radii", v)} disabled={loc.lat == null} defaultEta={Number(s.default_eta) || 0} />
        <AreaList areas={areas} editing={editing} onEdit={setEditing} onPatch={patchArea} onNew={newArea} defaultEta={Number(s.default_eta) || 0}
          onRemove={(i) => { setAreas(areas.filter((_, j) => j !== i)); setEditing(null); }} />
      </div>
      <p className="text-xs text-muted-foreground">Como a taxa é escolhida: se o endereço estiver dentro de uma área desenhada, vale a taxa dela (a primeira da lista). Se não, vale o menor raio que alcança o endereço. Fora de tudo, o pedido de entrega é bloqueado e o cliente é avisado. O prazo da área/raio aparece para o cliente antes de pedir e vira o prazo do pedido; vazio usa o prazo padrão.</p>
    </div>
  );
}
